import {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
} from "react";
import {
    onAuthStateChanged,
    User as FirebaseUser,
    signOut,
} from "firebase/auth";
// IMPORTANT: Import the same "auth" from the file where you set persistence
import { auth } from "../api/firebase";
// Import Firestore functions
import { doc, getDoc, getFirestore, onSnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

import { useUser } from "../hooks/useUser";

// Define an array of allowed student domains
const ALLOWED_STUDENT_DOMAINS = ["career-accelerator", "seeu", "ucla-bsn"]; // Replace with your actual domains

// Define subscription status types
export type SubscriptionStatus =
    | "ACTIVE"
    | "SUSPENDED"
    | "CANCELLED"
    | "EXPIRED"
    | "APPROVED"
    | "NONE";

// Define subscription plan types
export type SubscriptionPlan = {
    id: string;
    name: string;
    description: string;
    features: string[];
    price: number;
    currency: string;
    interval: string;
};

// Define subscription data type
export type SubscriptionData = {
    id: string;
    planId: string;
    status: SubscriptionStatus;
    createdAt: string;
    startTime: string;
    nextBillingTime: string | null;
    lastPaymentDate?: string;
    lastPaymentAmount?: string;
};

type AuthContextValue = {
    firebaseUser: FirebaseUser | null;
    userDoc: any;
    loading: boolean;
    subscription: SubscriptionData | null;
    subscriptionPlan: SubscriptionPlan | null;
    subscriptionLoading: boolean;
    checkSubscription: () => Promise<void>;
    hasActiveSubscription: boolean;
};

const AuthContext = createContext<AuthContextValue>({
    firebaseUser: null,
    userDoc: undefined,
    loading: true,
    subscription: null,
    subscriptionPlan: null,
    subscriptionLoading: true,
    checkSubscription: async () => {},
    hasActiveSubscription: false,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [subscription, setSubscription] = useState<SubscriptionData | null>(
        null
    );
    const [subscriptionPlan, setSubscriptionPlan] =
        useState<SubscriptionPlan | null>(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(true);
    const [userDoc, setUserDoc] = useState<any>(null);

    // Check if user has an active subscription
    const hasActiveSubscription =
        subscription?.status === "ACTIVE" ||
        subscription?.status === "APPROVED";

    // Function to check subscription status
    const checkSubscription = async () => {
        if (!firebaseUser) {
            setSubscription(null);
            setSubscriptionPlan(null);
            setSubscriptionLoading(false);
            return;
        }

        try {
            setSubscriptionLoading(true);
            const db = getFirestore();
            const userDocRef = doc(db, "users", firebaseUser.uid);
            const userDocSnap = await getDoc(userDocRef);

            if (!userDocSnap.exists()) {
                setSubscription(null);
                setSubscriptionPlan(null);
                setSubscriptionLoading(false);
                return;
            }

            const userData = userDocSnap.data();

            // If user has a subscription ID, get the subscription details
            if (userData.subscriptionId) {
                const subscriptionDocRef = doc(
                    db,
                    "subscriptions",
                    userData.subscriptionId
                );
                const subscriptionDocSnap = await getDoc(subscriptionDocRef);

                if (subscriptionDocSnap.exists()) {
                    const subscriptionData =
                        subscriptionDocSnap.data() as SubscriptionData;
                    setSubscription(subscriptionData);

                    // Now get the plan details if we have a planId
                    if (subscriptionData.planId) {
                        try {
                            // Use the Firebase function to get all plans
                            const functions = getFunctions();
                            const getPayPalPlansFunction = httpsCallable(
                                functions,
                                "getPayPalPlans"
                            );
                            const result = await getPayPalPlansFunction();
                            const data = result.data as any;
                            // Find the matching plan
                            const paypalPlan = data.plans.find(
                                (plan: any) =>
                                    plan.id === subscriptionData.planId
                            );

                            if (paypalPlan) {
                                // Find the product for this plan
                                const product = data.products.find(
                                    (p: any) => p.id === paypalPlan.product_id
                                );

                                // Extract features from product description
                                let features: string[] = [];
                                try {
                                    if (product?.description) {
                                        const featuresData = JSON.parse(
                                            product.description
                                        );
                                        if (
                                            Array.isArray(featuresData.features)
                                        ) {
                                            features = featuresData.features;
                                        }
                                    }
                                } catch (e) {
                                    features = product?.description
                                        ? [product.description]
                                        : [];
                                }

                                // Get price cycle
                                const priceCycle =
                                    paypalPlan.billing_cycles.find(
                                        (cycle: any) =>
                                            cycle.tenure_type === "REGULAR"
                                    );

                                const price = priceCycle
                                    ? parseFloat(
                                          priceCycle.pricing_scheme.fixed_price
                                              .value
                                      )
                                    : 0;

                                const interval = priceCycle
                                    ? `${
                                          priceCycle.frequency.interval_count
                                      } ${priceCycle.frequency.interval_unit.toLowerCase()}${
                                          priceCycle.frequency.interval_count >
                                          1
                                              ? "s"
                                              : ""
                                      }`
                                    : "";

                                setSubscriptionPlan({
                                    id: paypalPlan.id,
                                    name: paypalPlan.name,
                                    description: product?.name || "",
                                    features: features,
                                    price: price,
                                    currency:
                                        priceCycle?.pricing_scheme.fixed_price
                                            .currency_code || "USD",
                                    interval: interval,
                                });
                            }
                        } catch (error) {
                            console.error(
                                "Error fetching subscription plan details:",
                                error
                            );
                        }
                    }
                } else {
                    // Subscription doc doesn't exist, user may have incorrect reference
                    setSubscription(null);
                    setSubscriptionPlan(null);
                }
            } else {
                // User has no subscription
                setSubscription(null);
                setSubscriptionPlan(null);
            }
        } catch (error) {
            console.error("Error checking subscription:", error);
        } finally {
            setSubscriptionLoading(false);
        }
    };

    // Set up subscription listener
    useEffect(() => {
        if (!firebaseUser) return;

        const db = getFirestore();
        const userDocRef = doc(db, "users", firebaseUser.uid);

        // Single listener for both user and subscription changes
        const unsubscribe = onSnapshot(
            userDocRef,
            async (userSnapshot) => {
                if (!userSnapshot.exists()) return;

                const userData = userSnapshot.data();

                // Update user doc state
                setUserDoc(userData);

                // If user has a subscription, fetch it once (not as a listener)
                if (userData.subscriptionId) {
                    try {
                        const subscriptionDocRef = doc(
                            db,
                            "subscriptions",
                            userData.subscriptionId
                        );
                        const subscriptionSnap = await getDoc(
                            subscriptionDocRef
                        );

                        if (subscriptionSnap.exists()) {
                            setSubscription(
                                subscriptionSnap.data() as SubscriptionData
                            );
                        } else {
                            setSubscription(null);
                        }
                    } catch (error) {
                        console.error("Error fetching subscription:", error);
                        setSubscription(null);
                    }
                } else {
                    setSubscription(null);
                }
            },
            (error) => {
                console.error("Error in user document listener:", error);
            }
        );

        return () => unsubscribe();
    }, [firebaseUser]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setLoadingAuth(true);

            if (user) {
                if (!user.emailVerified) {
                    try {
                        const db = getFirestore();
                        const userDocRef = doc(db, "users", user.uid);
                        const userDocSnap = await getDoc(userDocRef);

                        if (userDocSnap.exists()) {
                            const userData = userDocSnap.data();
                            const studentFrom = userData.studentFrom;

                            if (
                                studentFrom &&
                                ALLOWED_STUDENT_DOMAINS.includes(studentFrom)
                            ) {
                                setFirebaseUser(user);
                                setLoadingAuth(false);
                                return;
                            }
                        }

                        await signOut(auth);
                        setFirebaseUser(null);
                    } catch (error) {
                        console.error("Error checking user document:", error);
                        await signOut(auth);
                        setFirebaseUser(null);
                    }
                } else {
                    setFirebaseUser(user);
                }
            } else {
                setFirebaseUser(null);
                setSubscription(null);
                setSubscriptionPlan(null);
                setUserDoc(null);
            }

            setLoadingAuth(false);
        });

        return () => unsubscribe();
    }, []);

    const loading = loadingAuth;

    return (
        <AuthContext.Provider
            value={{
                firebaseUser,
                userDoc,
                loading,
                subscription,
                subscriptionPlan,
                subscriptionLoading,
                checkSubscription,
                hasActiveSubscription,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
